import React from "react";
import { Button } from "@/components/ui/button";
import ProviderCard from "./ProviderCard";
import ComingSoonCard from "./ComingSoonCard";
import type {
  CloudProviderConfig,
  ComingSoonProvider,
} from "@/types/onboarding";

import { useProviderCredentials } from "@/hooks/useProviderCredentials";
import { msUntilExpiry } from "@/utils/expiry";

interface ProviderSelectionProps {
  configs: Record<string, CloudProviderConfig>;
  comingSoon: ComingSoonProvider[];
  showMore: boolean;
  onConnect: (providerId: string) => void;
  toggleShowMore: () => void;
}

const ProviderSelection: React.FC<ProviderSelectionProps> = ({
  configs,
  comingSoon,
  onConnect,
}) => {
  const { data: credentials } = useProviderCredentials();

  const determineStatus = (providerId: string) => {
    const providerCredentials = credentials?.filter(
      (c) => c.provider === providerId
    );

    if (!providerCredentials || providerCredentials.length === 0) {
      return "none" as const;
    }

    const hasExpired = providerCredentials.some(
      (cred) =>
        cred.last_validated_at && msUntilExpiry(cred.last_validated_at) <= 0
    );

    if (hasExpired) {
      return "expired" as const;
    }

    return "connected" as const;
  };

  return (
    <div className="flex items-center justify-center h-full p-6">
      <div className="w-full max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 justify-items-center px-10">
          {Object.entries(configs).map(([id, config]) => (
            <ProviderCard
              key={id}
              id={id}
              config={config}
              onConnect={onConnect}
              status={determineStatus(id)}
            />
          ))}
          {comingSoon.map((provider) => (
            <ComingSoonCard key={provider.id} provider={provider} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProviderSelection;
